import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import socketService from "../services/socketService";
import { playSound } from "../utils/soundManager";

const GameContext = createContext();

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error("useGame doit être utilisé dans un GameProvider");
  }
  return context;
};

export const GameProvider = ({ children }) => {
  const [roomId, setRoomId] = useState("");
  const [myPseudo, setMyPseudo] = useState("");
  const [gameState, setGameState] = useState("lobby");
  const [players, setPlayers] = useState([]);
  const [visibleCharacters, setVisibleCharacters] = useState([]);
  const [currentTurnPlayerId, setCurrentTurnPlayerId] = useState(null);
  const [hasSubmittedCharacter, setHasSubmittedCharacter] = useState([]);
  const [winners, setWinners] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [avatar, setAvatarState] = useState(() => {
    return localStorage.getItem("selectedAvatar") || null;
  });
  const [cancelVotes, setCancelVotes] = useState({
    votes: 0,
    total: 0,
    required: 0,
  });
  const [theme, setThemeState] = useState("");
  const [hintsEnabled, setHintsEnabledState] = useState(false);
  const [hintRequests, setHintRequests] = useState([]);
  const [revealedHints, setRevealedHints] = useState([]);
  const [myHint, setMyHint] = useState(null);
  const [isSpectator, setIsSpectator] = useState(false);
  const [spectatorCount, setSpectatorCount] = useState(0);
  const [randomMode, setRandomModeState] = useState(false);
  const [turnNumber, setTurnNumber] = useState(0);
  const [lastWinner, setLastWinner] = useState(null);
  const [turnOrder, setTurnOrder] = useState([]);
  const [darkMode, setDarkModeState] = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    const newValue = !darkMode;
    setDarkModeState(newValue);
    localStorage.setItem("darkMode", String(newValue));
  };

  // Tracker pour détecter les nouveaux joueurs
  const previousPlayersRef = useRef([]);

  useEffect(() => {
    // Charger le playerId sauvegardé avant de se connecter
    socketService.loadSavedPlayerId();
    socketService.connect();

    // Écouter les mises à jour de la room
    socketService.onRoomUpdate((data) => {
      setGameState(data.state);
      setPlayers(data.players);
      setCurrentTurnPlayerId(data.currentTurnPlayerId);
      setHasSubmittedCharacter(data.hasSubmittedCharacter || []);
      setThemeState(data.theme || "");
      setHintsEnabledState(data.hintsEnabled || false);
      setHintRequests(data.hintRequests || []);
      setRevealedHints(data.revealedHints || []);
      setSpectatorCount(data.spectatorCount || 0);
      setRandomModeState(data.randomMode || false);
      setTurnNumber(data.turnNumber || 0);
      setTurnOrder(data.turnOrder || []);

      // Mettre à jour myPseudo si on se reconnecte
      const myPlayerId = socketService.getPlayerId();
      const myPlayer = data.players.find((p) => p.id === myPlayerId);
      if (myPlayer?.pseudo) {
        setMyPseudo(myPlayer.pseudo);
      }
    });

    // Écouter le démarrage du jeu
    socketService.onGameStarted((data) => {
      addNotification(data.message, "success");
    });

    // Écouter l'attribution des personnages
    socketService.onCharactersAssigned((data) => {
      setVisibleCharacters(data.visibleCharacters);
      addNotification("Les personnages ont été attribués !", "success");
    });

    // Écouter les changements de tour
    socketService.onTurnChanged((data) => {
      setCurrentTurnPlayerId(data.currentPlayerId);
      setTurnNumber(data.turnNumber || 0);
      const myId = socketService.getPlayerId();
      if (data.currentPlayerId === myId) {
        addNotification("C'est votre tour !", "info");
        playSound("your-turn.mp3");
      }
    });

    // Écouter les victoires
    socketService.onPlayerWon((data) => {
      addNotification(
        `${data.pseudo} a trouvé son personnage (${data.character}) ! Position: ${data.position}`,
        "success",
      );
      setLastWinner(data);
      setTimeout(() => setLastWinner(null), 3000);
    });

    // Écouter la fin de partie
    socketService.onGameFinished((data) => {
      setWinners(data.winners);
      setGameState("finished");
      addNotification("La partie est terminée !", "success");
    });
    // Écouter les propositions incorrectes
    socketService.onWrongGuess((data) => {
      addNotification(data.message, "error");
    });

    // Écouter les déconnexions
    socketService.onPlayerDisconnected((data) => {
      playSound("player-leave.mp3");
      addNotification(`${data.pseudo} s'est déconnecté`, "warning");
    });

    // Écouter les reconnexions
    socketService.onPlayerReconnected((data) => {
      playSound("player-join.mp3");
      addNotification(`${data.pseudo} s'est reconnecté`, "success");
    });

    // Écouter les suppressions de joueurs
    socketService.onPlayerRemoved((data) => {
      playSound("player-leave.mp3");
      addNotification(`${data.pseudo} a été retiré de la partie`, "error");
    });

    // Écouter les votes d'annulation
    socketService.onCancelVoteUpdate((data) => {
      setCancelVotes(data);
    });

    // Écouter l'annulation de la room
    socketService.onRoomCancelled((data) => {
      addNotification(data.message, "error");
      // Nettoyer complètement la session
      socketService.clearSession();
      setRoomId("");
      setMyPseudo("");
      setGameState("lobby");
      setPlayers([]);
      setVisibleCharacters([]);
      setCancelVotes({ votes: 0, total: 0, required: 0 });
      setThemeState("");
      setHintsEnabledState(false);
      setMyHint(null);
      setHintRequests([]);
      setRevealedHints([]);
      setIsSpectator(false);
      setSpectatorCount(0);
      setRandomModeState(false);
      // Redirection vers la home sera gérée par le composant
    });

    // Écouter la reconnexion réussie
    socketService.onReconnectSuccess((data) => {
      console.log("Reconnexion réussie détectée dans le contexte", data);
      setRoomId(data.roomId);
      if (data.pseudo) {
        setMyPseudo(data.pseudo);
      }
      addNotification(
        `Reconnexion réussie à la room ${data.roomId}`,
        "success",
      );
    });

    // Écouter si le joueur est kické
    socketService.onPlayerKicked((data) => {
      addNotification(data.message, "error");
      // Nettoyer complètement la session
      socketService.clearSession();
      setRoomId("");
      setMyPseudo("");
      setGameState("lobby");
      setPlayers([]);
      setVisibleCharacters([]);
      setCancelVotes({ votes: 0, total: 0, required: 0 });
      setThemeState("");
      setHintsEnabledState(false);
      setMyHint(null);
      setHintRequests([]);
      setRevealedHints([]);
      setIsSpectator(false);
      setSpectatorCount(0);
      setRandomModeState(false);
      // Redirection vers la home sera gérée par le composant
    });

    socketService.onHintRequested((data) => {
      const myId = socketService.getPlayerId();
      if (data.playerId !== myId) {
        addNotification(`${data.pseudo} demande son indice`, "hint_request", {
          persistent: true,
          targetPlayerId: data.playerId,
          pseudo: data.pseudo,
        });
      }
    });

    socketService.onHintRevealed((data) => {
      setMyHint(data.hint);
      addNotification(`Votre indice : ${data.hint}`, "success");
    });

    socketService.onHintApproved((data) => {
      setNotifications((prev) =>
        prev.filter((n) => !(n.type === "hint_request" && n.targetPlayerId === data.playerId))
      );
      addNotification(`Indice révélé à ${data.pseudo}`, "info");
    });

    // Dismiss la notification chez le joueur qui a refusé (lui seul)
    socketService.onHintRejectedByMe((data) => {
      setNotifications((prev) =>
        prev.filter((n) => !(n.type === "hint_request" && n.targetPlayerId === data.playerId))
      );
    });

    // Tous les joueurs ont refusé → la demande est annulée
    socketService.onHintRejected((data) => {
      setNotifications((prev) =>
        prev.filter((n) => !(n.type === "hint_request" && n.targetPlayerId === data.playerId))
      );
      addNotification(`Demande d'indice de ${data.pseudo} refusée par tous`, "error");
    });

    // Écouter la confirmation de spectateur
    socketService.onSpectatorJoined((data) => {
      setIsSpectator(true);
      setRoomId(data.roomId);
    });

    // Forcer la sortie de la room
    socketService.onForceLeaveRoom(() => {
      socketService.clearSession();
      setRoomId("");
      setMyPseudo("");
      setGameState("lobby");
      setPlayers([]);
      setVisibleCharacters([]);
      setCancelVotes({ votes: 0, total: 0, required: 0 });
      setThemeState("");
      setHintsEnabledState(false);
      setMyHint(null);
      setHintRequests([]);
      setRevealedHints([]);
      setIsSpectator(false);
      setSpectatorCount(0);
      setRandomModeState(false);
    });

    return () => {
      socketService.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Détecter les nouveaux joueurs et jouer le son
  useEffect(() => {
    const newPlayerIds = players.map((p) => p.id);
    const previousPlayerIds = previousPlayersRef.current.map((p) => p.id);

    // Trouver les nouveaux joueurs (IDs qui n'étaient pas là avant)
    const addedPlayerIds = newPlayerIds.filter(
      (id) => !previousPlayerIds.includes(id),
    );

    // Jouer le son pour chaque nouveau joueur (excluant soi-même)
    const myPlayerId = socketService.getPlayerId();
    addedPlayerIds.forEach((playerId) => {
      if (playerId !== myPlayerId && gameState === "lobby") {
        playSound("player-join.mp3");
      }
    });

    // Mettre à jour la référence
    previousPlayersRef.current = players;
  }, [players, gameState]);

  const joinRoom = (roomId) => {
    setRoomId(roomId);
    socketService.joinRoom(roomId);
  };

  const setPseudo = (pseudo) => {
    setMyPseudo(pseudo);
    socketService.setPseudo(pseudo);
  };

  const setAvatar = (avatarId) => {
    setAvatarState(avatarId);
    localStorage.setItem("selectedAvatar", avatarId);
    socketService.setAvatar(avatarId);
  };

  const setReady = () => {
    socketService.playerReady();
  };

  const submitCharacter = (characterName, hint) => {
    socketService.submitCharacter(characterName, hint);
  };

  const passTurn = () => {
    socketService.passTurn();
  };

  const guessCharacter = (guess) => {
    socketService.guessCharacter(guess);
  };

  const voteToCancel = () => {
    socketService.voteToCancel();
  };

  const kickPlayer = (targetPlayerId) => {
    socketService.kickPlayer(targetPlayerId);
  };

  const setTheme = (theme) => {
    socketService.setTheme(theme);
  };

  const setHintsEnabled = (enabled) => {
    socketService.setHintsEnabled(enabled);
  };

  const requestHint = () => {
    socketService.requestHint();
  };

  const approveHintRequest = (targetPlayerId) => {
    socketService.approveHintRequest(targetPlayerId);
  };

  const rejectHintRequest = (targetPlayerId) => {
    socketService.rejectHintRequest(targetPlayerId);
  };

  const joinAsSpectator = (roomId) => {
    setRoomId(roomId);
    setIsSpectator(true);
    socketService.joinAsSpectator(roomId);
  };

  const setRandomMode = (enabled) => {
    socketService.setRandomMode(enabled);
  };

  const resetGameState = () => {
    setGameState("lobby");
    setWinners([]);
    setHasSubmittedCharacter([]);
    setVisibleCharacters([]);
    setMyHint(null);
    setHintRequests([]);
    setRevealedHints([]);
    setIsSpectator(false);
    setSpectatorCount(0);
    setTurnNumber(0);
    setLastWinner(null);
    setTurnOrder([]);
  };

  const launchNewRound = () => {
    socketService.launchNewRound();
    resetGameState();
  };

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const addNotification = (message, type = "info", extra = {}) => {
    const id = Date.now() + Math.random();
    setNotifications((prev) => [...prev, { id, message, type, ...extra }]);
    if (!extra.persistent) {
      setTimeout(() => {
        removeNotification(id);
      }, 5000);
    }
  };

  const getMyPlayer = () => {
    const myPlayerId = socketService.getPlayerId();
    return players.find((p) => p.id === myPlayerId);
  };

  const isMyTurn = () => {
    const myPlayerId = socketService.getPlayerId();
    return currentTurnPlayerId === myPlayerId;
  };

  const getCurrentPlayer = () => {
    return players.find((p) => p.id === currentTurnPlayerId);
  };

  const prepareReconnect = () => {
    // Définir le roomId sauvegardé pour éviter la redirection
    const savedRoomId = localStorage.getItem("roomId");
    if (savedRoomId) {
      setRoomId(savedRoomId);
    }
  };

  const value = {
    roomId,
    myPseudo,
    gameState,
    players,
    visibleCharacters,
    currentTurnPlayerId,
    hasSubmittedCharacter,
    winners,
    notifications,
    cancelVotes,
    avatar,
    theme,
    hintsEnabled,
    hintRequests,
    revealedHints,
    myHint,
    joinRoom,
    setPseudo,
    setAvatar,
    setReady,
    submitCharacter,
    passTurn,
    guessCharacter,
    voteToCancel,
    kickPlayer,
    getMyPlayer,
    isMyTurn,
    getCurrentPlayer,
    resetGameState,
    launchNewRound,
    prepareReconnect,
    setTheme,
    setHintsEnabled,
    requestHint,
    approveHintRequest,
    rejectHintRequest,
    removeNotification,
    isSpectator,
    spectatorCount,
    randomMode,
    turnNumber,
    lastWinner,
    turnOrder,
    joinAsSpectator,
    setRandomMode,
    darkMode,
    toggleDarkMode,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};
